import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface AppConfiguration {
    id: number;
    name: string;
    value: string;
    created_at?: string;
    updated_at?: string;
}

export interface AppSettingsState {
    operatorElectronicSignatureEnabled: boolean;
    loaded: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class AppSettingsService {
    private basePath = environment.interop.basePath;
    private findConfigUrl = this.basePath + 'api/registratura/configuration/find/';

    // Cache for settings
    private settingsCache = new Map<string, any>();
    private cacheExpiry = new Map<string, number>();
    private cacheDuration = 5 * 60 * 1000; // 5 minutes cache

    // BehaviorSubject for reactive settings state
    private settingsState = new BehaviorSubject<AppSettingsState>({
        operatorElectronicSignatureEnabled: false,
        loaded: false
    });

    public settings$ = this.settingsState.asObservable();

    constructor(private http: HttpClient) {
        // Load settings on service initialization
        this.loadSettings();
    }

    /**
     * Load all relevant settings
     */
    loadSettings(): void {
        this.getConfigByName('operator_electronic_signature_enabled').subscribe({
            next: (value) => {
                this.settingsState.next({
                    operatorElectronicSignatureEnabled: value === '1' || value === 'true',
                    loaded: true
                });
            },
            error: () => {
                // Default to disabled if error
                this.settingsState.next({
                    operatorElectronicSignatureEnabled: false,
                    loaded: true
                });
            }
        });
    }

    /**
     * Get configuration by name from backend
     */
    getConfigByName(name: string): Observable<string> {
        // Check cache first
        const cached = this.getFromCache(name);
        if (cached !== null) {
            return of(cached);
        }

        return this.http.get<any>(this.findConfigUrl + name).pipe(
            map(response => {
                const value = response?.data?.value || '';
                this.setCache(name, value);
                return value;
            }),
            catchError(error => {
                console.error(`Error fetching config ${name}:`, error);
                return of('');
            })
        );
    }

    /**
     * Check if operator electronic signature is enabled
     */
    isOperatorElectronicSignatureEnabled(): boolean {
        return this.settingsState.value.operatorElectronicSignatureEnabled;
    }

    /**
     * Get observable for operator electronic signature enabled state
     */
    getOperatorElectronicSignatureEnabled$(): Observable<boolean> {
        return this.settings$.pipe(
            map(state => state.operatorElectronicSignatureEnabled)
        );
    }

    /**
     * Check if settings have been loaded
     */
    isLoaded(): boolean {
        return this.settingsState.value.loaded;
    }

    /**
     * Refresh settings from server (clear cache and reload)
     */
    refreshSettings(): void {
        this.clearCache();
        this.loadSettings();
    }

    /**
     * Update the electronic signature setting locally (after admin update)
     */
    updateElectronicSignatureEnabled(enabled: boolean): void {
        this.setCache('operator_electronic_signature_enabled', enabled ? '1' : '0');
        this.settingsState.next({
            ...this.settingsState.value,
            operatorElectronicSignatureEnabled: enabled
        });
    }

    // Cache management methods
    private getFromCache(key: string): string | null {
        const expiry = this.cacheExpiry.get(key);
        if (expiry && Date.now() < expiry) {
            return this.settingsCache.get(key) || null;
        }
        // Cache expired or not found
        this.settingsCache.delete(key);
        this.cacheExpiry.delete(key);
        return null;
    }

    private setCache(key: string, value: string): void {
        this.settingsCache.set(key, value);
        this.cacheExpiry.set(key, Date.now() + this.cacheDuration);
    }

    private clearCache(): void {
        this.settingsCache.clear();
        this.cacheExpiry.clear();
    }
}
